import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, Text, Button, Shelf } from "@chaser/components";
import { calculateTieredPrice } from "../utils/calculateTieredPrice";
import { ADDON_TYPES } from "../../../util/constants";
import { toCurrency } from "../../../util";
import styles from "./AddonOption.module.scss";
import BulkPricingPopover from "./components/BulkPricingPopover";
import QuantityControls from "./components/QuantityControls";
import VolumePricingBlock from "./components/VolumePricingBlock";
import PriceSummary from "./components/PriceSummary";
import {
  clampToStep,
  findCurrentTier,
  getDefaultQuantity,
  getMaxBulkDiscount,
  getMaxTierPrice,
  getNextTier,
  getQuantityLabel,
  getResolvedDescription,
  getSavingsPercent,
  getSliderPercent,
  getSliderStyle,
  getUniqueTiers,
} from "./utils";

const AddonOption = ({
  description = "",
  icon,
  info,
  name,
  type,
  quantity,
  minQuantity = 1,
  maxQuantity = 10000,
  quantityStep = 1,
  setQuantity,
  price = 0,
  currency,
  tiers = [],
  period,
  scheduled = null,
  onCancelScheduledChange = null,
  isLoadingCancel = false,
}) => {
  const resolvedQuantity = quantity ?? 0;
  const isScheduledForRemoval =
    scheduled &&
    scheduled.overages === 0 &&
    Number(scheduled.overagesChange) < 0;
  const [calculatedPrice, setCalculatedPrice] = useState(() =>
    toCurrency((price || 0) / 100, currency),
  );
  const [quantityInput, setQuantityInput] = useState(
    String(resolvedQuantity || ""),
  );
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [bulkPopoverOpen, setBulkPopoverOpen] = useState(false);
  const hasTieredPrice = !!tiers?.length;
  const isFlatFee = type === ADDON_TYPES.FLAT_FEE;
  const isVolume = type === ADDON_TYPES.VOLUME;
  const resolvedDescription = getResolvedDescription(description, isVolume);
  const didInitDefaultQuantity = useRef(false);

  const isQuantityEditable = useCallback(() => !isFlatFee, [isFlatFee]);

  const quantityLabel = useMemo(() => {
    return getQuantityLabel(name, resolvedQuantity);
  }, [name, resolvedQuantity]);
  const volumeQuantityLabel = period ? `credits / ${period}` : "credits";
  const allowanceStyleLabel = period
    ? `${quantityLabel} / ${period}`
    : quantityLabel;

  const calculatePrice = useCallback(
    (quantity) => {
      return calculateTieredPrice(quantity, tiers, price);
    },
    [tiers, price],
  );
  useEffect(() => {
    const totalPrice = calculatePrice(isFlatFee ? 1 : resolvedQuantity);
    const formattedPrice = `${toCurrency(totalPrice, currency)}`;
    setCalculatedPrice(formattedPrice);
  }, [resolvedQuantity, calculatePrice, isFlatFee, currency]);

  useEffect(() => {
    if (isEditingQuantity) return;
    setQuantityInput(String(resolvedQuantity || ""));
  }, [isEditingQuantity, resolvedQuantity]);

  const uniqueTiers = useMemo(() => {
    return getUniqueTiers(tiers);
  }, [tiers]);

  const clampQuantity = useCallback(
    (rawValue) => clampToStep(rawValue, minQuantity, maxQuantity, quantityStep),
    [maxQuantity, minQuantity, quantityStep],
  );

  const handleSliderChange = (e) => {
    if (isFlatFee) return;
    const next = clampQuantity(parseInt(e.target.value, 10));
    setQuantity(next);
  };

  const commitQuantityInput = useCallback(() => {
    if (isFlatFee) return;

    const trimmed = String(quantityInput || "").trim();
    if (!trimmed) {
      setQuantityInput(String(quantity));
      return;
    }

    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed)) {
      setQuantityInput(String(quantity));
      return;
    }

    const next = clampQuantity(parsed);
    setQuantity(next);
  }, [clampQuantity, isFlatFee, quantity, quantityInput, setQuantity]);

  const handleQuantityInputChange = (e) => {
    if (isFlatFee) return;
    const raw = String(e.target.value ?? "");
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    setQuantityInput(digitsOnly);

    if (!digitsOnly) return;
    const parsed = parseInt(digitsOnly, 10);
    if (Number.isNaN(parsed)) return;
    setQuantity(clampQuantity(parsed));
  };

  const maxTierPrice = useMemo(
    () => getMaxTierPrice(uniqueTiers),
    [uniqueTiers],
  );

  const maxBulkDiscount = useMemo(
    () => getMaxBulkDiscount(uniqueTiers),
    [uniqueTiers],
  );

  const currentTier = useMemo(
    () => findCurrentTier(uniqueTiers, resolvedQuantity),
    [resolvedQuantity, uniqueTiers],
  );
  const currentTierPrice = currentTier?.price ?? null;

  const currentSavingsPercent = useMemo(() => {
    return getSavingsPercent(maxTierPrice, currentTierPrice);
  }, [currentTierPrice, maxTierPrice]);

  const sliderPercent = useMemo(() => {
    return getSliderPercent(resolvedQuantity, minQuantity, maxQuantity);
  }, [maxQuantity, minQuantity, resolvedQuantity]);

  useEffect(() => {
    if (didInitDefaultQuantity.current) return;
    if (isFlatFee) return;
    if (typeof setQuantity !== "function") return;
    if (resolvedQuantity > 0) return;

    const next = getDefaultQuantity(minQuantity, maxQuantity, quantityStep);
    if (next == null) return;
    didInitDefaultQuantity.current = true;
    setQuantity(next);
  }, [
    isFlatFee,
    maxQuantity,
    minQuantity,
    quantityStep,
    resolvedQuantity,
    setQuantity,
  ]);

  const sliderStyle = useMemo(
    () => getSliderStyle(sliderPercent, true),
    [sliderPercent],
  );

  const nextTier = useMemo(() => {
    return getNextTier(uniqueTiers, resolvedQuantity);
  }, [resolvedQuantity, uniqueTiers]);

  const quantityToNextTier = useMemo(() => {
    if (!nextTier) return 0;
    return Math.max(0, nextTier.startingUnit - resolvedQuantity);
  }, [nextTier, resolvedQuantity]);

  const currentTierKey = currentTier
    ? `${currentTier.price}-${currentTier.startingUnit}`
    : null;

  return (
    <Stack width="100%" height="100%" gap="small" maxWidth="100%">
      {resolvedDescription && (
        <Text
          fontSize="small"
          textAlign="left"
          color="Mid Neutral 1"
          className={styles.volumeDescription}
        >
          {resolvedDescription}
        </Text>
      )}
      {isScheduledForRemoval && onCancelScheduledChange && (
        <Stack gap="small" className={styles.scheduledRemovalNotice} p="small">
          <Shelf gap="small" justifyContent="space-between" alignItems="center">
            <Text color="Red" fontSize="small" fontWeight="bold">
              × Scheduled for removal
            </Text>
            <Button
              appearance="ghost"
              size="narrow"
              loading={isLoadingCancel}
              onClick={onCancelScheduledChange}
              data-trackingid="addon-option-cancel-removal"
            >
              Cancel
            </Button>
          </Shelf>
        </Stack>
      )}
      {isQuantityEditable() && (
        <Stack gap="medium">
          {hasTieredPrice && !isVolume && (
            <BulkPricingPopover
              open={bulkPopoverOpen}
              onToggle={setBulkPopoverOpen}
              tierContext={{
                maxBulkDiscount,
                uniqueTiers,
                minQuantity,
                maxQuantity,
                maxTierPrice,
                resolvedQuantity,
              }}
              onSelectTier={(startingUnit) => {
                setQuantity(clampQuantity(Math.max(startingUnit, minQuantity)));
              }}
              currency={currency}
            />
          )}

          <QuantityControls
            config={{
              isVolume,
              currentSavingsPercent,
              resolvedQuantity,
              quantityStep,
              minQuantity,
              maxQuantity,
              volumeQuantityLabel,
              allowanceStyleLabel,
              quantityLabel,
              info,
              sliderStyle,
              sliderPercent,
            }}
            state={{
              quantityInput,
            }}
            handlers={{
              onQuantityInputChange: handleQuantityInputChange,
              onQuantityInputFocus: () => setIsEditingQuantity(true),
              onQuantityInputBlur: () => {
                setIsEditingQuantity(false);
                commitQuantityInput();
              },
              onQuantityInputKeyDown: (e) => {
                if (e.key === "Enter") {
                  commitQuantityInput();
                  e.currentTarget.blur();
                }
              },
              setQuantity,
              clampToStep: clampQuantity,
              handleSliderChange,
            }}
          />

          {isVolume && hasTieredPrice && (
            <VolumePricingBlock
              tierContext={{
                uniqueTiers,
                currentTierKey,
                minQuantity,
                nextTier,
                quantityToNextTier,
              }}
              onSelectTier={(nextQuantity) => {
                setQuantity(clampQuantity(nextQuantity));
              }}
              currency={currency}
            />
          )}
        </Stack>
      )}
      <PriceSummary
        resolvedQuantity={resolvedQuantity}
        isVolume={isVolume}
        currentTier={currentTier}
        price={price}
        currency={currency}
        currentSavingsPercent={currentSavingsPercent}
        calculatedPrice={calculatedPrice}
        period={period}
      />
    </Stack>
  );
};

export default AddonOption;
